import React from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVertical, LayoutList, RotateCcw, ChevronUp, ChevronDown, Lock, Eye, EyeOff } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { HOME_WIDGETS, useHomeLayout } from '../../lib/homeLayout';

const BY_ID = Object.fromEntries(HOME_WIDGETS.map((w) => [w.id, w]));

function WidgetRow({ id, index, total, hidden, onToggle, onMove }) {
  const w = BY_ID[id];
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={id}
      dragListener={false}
      dragControls={controls}
      layout
      whileDrag={{ scale: 1.02, boxShadow: '0 10px 24px -8px hsl(var(--brand-ink) / 0.25)' }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
      className={`relative flex items-center gap-2 rounded-xl border bg-card p-2 pl-1 select-none ${hidden ? 'border-dashed border-border opacity-70' : 'border-border'}`}
      data-testid={`home-layout-item-${id}`}
      aria-label={`${w.label}, position ${index + 1} sur ${total}`}
    >
      <button
        type="button"
        aria-label={`Déplacer « ${w.label} »`}
        onPointerDown={(e) => { e.preventDefault(); controls.start(e); }}
        className="h-11 w-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary cursor-grab active:cursor-grabbing touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
        data-testid={`home-layout-handle-${id}`}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium flex items-center gap-1.5">
          {w.label}
          {w.locked && <Lock className="h-3 w-3 text-muted-foreground" aria-label="Toujours visible" />}
        </p>
        <p className="text-xs text-muted-foreground truncate">{w.description}</p>
      </div>
      <div className="flex items-center gap-0.5">
        <Button variant="ghost" size="icon" className="h-9 w-8" aria-label="Monter" disabled={index === 0} onClick={() => onMove(id, -1)} data-testid={`home-layout-up-${id}`}><ChevronUp className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-9 w-8" aria-label="Descendre" disabled={index === total - 1} onClick={() => onMove(id, 1)} data-testid={`home-layout-down-${id}`}><ChevronDown className="h-4 w-4" /></Button>
      </div>
      <Switch
        checked={!hidden}
        disabled={w.locked}
        onCheckedChange={(checked) => onToggle(id, !checked)}
        aria-label={hidden ? `Afficher « ${w.label} »` : `Masquer « ${w.label} »`}
        data-testid={`home-layout-toggle-${id}`}
      />
    </Reorder.Item>
  );
}

/** GRH › Paramètres: personal order and visibility of the Accueil widgets (drag & drop, this browser only). */
export function HomeLayoutCard({ onPreview }) {
  const { layout, setOrder, setHidden, move, reset, isDefault } = useHomeLayout();
  const visibleCount = layout.order.length - layout.hidden.length;

  const onReset = () => { reset(); toast.success('Accueil remis dans l\'ordre d\'origine'); };
  const onToggle = (id, hide) => {
    setHidden(id, hide);
    toast.info(hide ? `« ${BY_ID[id].label} » masqué de l'accueil` : `« ${BY_ID[id].label} » affiché sur l'accueil`);
  };

  return (
    <Card className="p-4" data-testid="settings-home-layout-card">
      <div className="flex items-start gap-3">
        <span className="h-11 w-11 rounded-xl bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><LayoutList className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">Organisation de l'accueil</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            Glissez les blocs avec la poignée <GripVertical className="inline h-3.5 w-3.5 align-text-bottom" /> pour changer leur ordre, et masquez ceux dont vous n'avez pas besoin. Réglage propre à ce téléphone.
          </p>
        </div>
      </div>

      <Reorder.Group axis="y" values={layout.order} onReorder={setOrder} className="mt-4 space-y-2" data-testid="home-layout-list">
        {layout.order.map((id, i) => (
          <WidgetRow key={id} id={id} index={i} total={layout.order.length} hidden={layout.hidden.includes(id)} onToggle={onToggle} onMove={move} />
        ))}
      </Reorder.Group>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="home-layout-visible-count">
          {layout.hidden.length ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {visibleCount} bloc{visibleCount > 1 ? 's' : ''} affiché{visibleCount > 1 ? 's' : ''}{layout.hidden.length ? ` · ${layout.hidden.length} masqué${layout.hidden.length > 1 ? 's' : ''}` : ''}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-9" onClick={onReset} disabled={isDefault} data-testid="home-layout-reset-button"><RotateCcw className="h-4 w-4 mr-1" /> Ordre d'origine</Button>
          {onPreview && <Button variant="outline" size="sm" className="h-9" onClick={onPreview} data-testid="home-layout-preview-button">Voir l'accueil</Button>}
        </div>
      </div>
    </Card>
  );
}
